import { PropsWithChildren } from 'react'
import {
  QueryClient,
  QueryClientProvider as TanStackQueryProvider,
} from '@tanstack/react-query'
import { client } from '@/generated/services.gen'
import { get } from 'lodash'

const getBaseUri = () => {
  if (process.env.REACT_APP_ENV === 'staging') {
    return 'https://staging-api.hydrascore.com'
  }
  if (process.env.REACT_APP_ENV === 'dev') {
    return 'https://dev-api.hydrascore.com'
  }
  if (process.env.NODE_ENV === 'production') {
    return 'https://api.hydrascore.com'
  }
  return (
    document.location.protocol + '//' + document.location.hostname + ':4011'
  )
}

const HANDLED_ERROR_CODES = [400, 403, 404, 500]

client.setConfig({
  baseURL: getBaseUri(),
  headers: {
    'Content-Type': 'application/json',
  },
})

client.instance.interceptors.request.use((config) => {
  const auth = JSON.parse(localStorage.getItem('auth') || '{}')
  const sessionKey = get(auth, 'sessionKey', '')
  if (sessionKey) {
    config.headers.set('Authorization', `Bearer ${sessionKey}`)
  }

  if (
    config.url?.includes('export') ||
    config.url?.includes('download_all_invoices')
  ) {
    config.responseType = 'arraybuffer'
  }
  return config
})

client.instance.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth')
      window.location.replace('/sign-in')
    }
    if (HANDLED_ERROR_CODES.includes(error.response?.status)) {
      const errorMessage =
        typeof error.response?.data['detail'] === 'string'
          ? error.response.data['detail']
          : error.toString()
      error.message = errorMessage
      return Promise.reject(error)
    }

    return Promise.reject(error)
  }
)

export default function QueryClientProvider({ children }: PropsWithChildren) {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
        refetchOnWindowFocus: false,
      },
    },
  })

  return (
    <TanStackQueryProvider client={queryClient}>
      {children}
    </TanStackQueryProvider>
  )
}
